import * as React from 'react';
import { Link } from 'react-router-dom';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import NotificationsNone from '@mui/icons-material/NotificationsNone';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { IconMap, TopBarProps } from '../Interfaces';
import KeyboardArrowIcon from './KeyboardArrowIcon';
import { useBreakpoint } from '../../utils/useBreakpoint';
import styles from '../Header.styles';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import { Divider } from '@mui/material';

const primaryMainSvgFilter =
  'invert(10%) sepia(82%) saturate(7095%) hue-rotate(228deg) brightness(92%) contrast(90%)';

const iconMap: IconMap = {
  account_management: <BadgeOutlinedIcon sx={styles.subMenuIcon} />,
  profile_settings: <SettingsOutlinedIcon sx={styles.subMenuIcon} />,
};

const normalizeLabel = (label: string): string =>
  label.toLowerCase().replace(/\s+/g, '_');

interface CustomBadgeProps {
  invisible: boolean;
  children: any;
  number?: number;
}
const CustomBadge: React.FC<CustomBadgeProps> = ({
  invisible,
  children,
  number,
}: CustomBadgeProps) => (
  <Badge
    badgeContent={number}
    invisible={invisible}
    classes={{ badge: 'header-badge' }}
    sx={styles.iconButtonBadge}
  >
    {children}
  </Badge>
);

const MenuIcon = ({
  externalUrl,
  url,
  hasUnread,
  nrOfUnread,
  icon,
}: {
  externalUrl: boolean;
  url: string;
  hasUnread: boolean;
  nrOfUnread?: number;
  icon: React.ReactNode;
}) => (
  <>
    <IconButton
      {...{
        href: externalUrl ? url : undefined,
        to: !externalUrl ? url : undefined,
        component: externalUrl ? 'a' : Link,
      }}
      disableTouchRipple
      disableRipple
      disableFocusRipple
      sx={styles.iconButton}
      size="small"
    >
      <CustomBadge invisible={!hasUnread} number={nrOfUnread}>
        {icon}
      </CustomBadge>
    </IconButton>
  </>
);

const TopBar: React.FC<TopBarProps> = ({
  isLoggedIn,
  accountLinks,
  messages,
  notifications,
  buttonsSettings,
  hideLoginRegister,
}: TopBarProps) => {
  const [profileAnchorEl, setprofileAnchorEl] = React.useState(null);
  const smUpBreakpoint = useBreakpoint('smUp');

  const openProfileMenu = (event: any) =>
    setprofileAnchorEl(event.currentTarget);
  const closeProfileMenu = () => setprofileAnchorEl(null);

  if (isLoggedIn)
    return (
      <>
        {smUpBreakpoint && (
          <MenuIcon
            externalUrl={!!messages.externalUrl}
            url={messages.url}
            hasUnread={messages.hasMessages}
            nrOfUnread={messages.nrOfUnread}
            icon={<MailOutlineIcon data-testid="MessagesOutlinedIcon" />}
          />
        )}
        <MenuIcon
          externalUrl={!!notifications.externalUrl}
          url={notifications.url}
          hasUnread={notifications.hasNotifications}
          nrOfUnread={notifications.nrOfUnread}
          icon={<NotificationsNone data-testid="NotificationsOutlinedIcon" />}
        />
        {smUpBreakpoint && (
          <>
            <Divider orientation="vertical" sx={styles.topbarDropdownDivider} />
            <Button
              onClick={openProfileMenu}
              aria-haspopup="true"
              aria-expanded={profileAnchorEl ? 'true' : undefined}
              color="ghost"
              size="small"
              sx={styles.topbarDropdown(profileAnchorEl, primaryMainSvgFilter)}
              disableTouchRipple
              disableFocusRipple
            >
              My Account
              <KeyboardArrowIcon
                className="arrow-icon"
                isOpen={!!profileAnchorEl}
              />
            </Button>
          </>
        )}
        {smUpBreakpoint && (
          <Menu
            anchorEl={profileAnchorEl}
            open={!!profileAnchorEl}
            onClose={closeProfileMenu}
            MenuListProps={{
              'aria-labelledby': 'basic-button',
            }}
            sx={styles.subMenu}
            classes={{
              paper: 'top-bar-menu',
            }}
          >
            {accountLinks?.map((link) => (
              <MenuItem
                onClick={(e: React.MouseEvent<HTMLElement>) => {
                  if (link.onClick) link.onClick(e);
                  closeProfileMenu();
                }}
                {...{
                  href: link.externalUrl ? link.url : undefined,
                  to: !link.externalUrl ? link.url : undefined,
                  component: link.externalUrl ? 'a' : Link,
                }}
                key={link.label}
                sx={styles.subMenuLink}
                disabled={link.disabled}
                disableTouchRipple
              >
                {link.label}{' '}
                {link.iconUrl &&
                  (iconMap[normalizeLabel(link.label)] || (
                    <LogoutOutlinedIcon sx={styles.subMenuIcon} />
                  ))}
                {link.icon && <link.icon />}
              </MenuItem>
            ))}
          </Menu>
        )}
      </>
    );

  return !hideLoginRegister
    ? (smUpBreakpoint && (
        <>
          <Button
            href={buttonsSettings.registerLink}
            onClick={(e) =>
              buttonsSettings.onRegisterClick &&
              buttonsSettings.onRegisterClick(e)
            }
            variant="outlined"
            color="danger"
            size="medium"
            data-testid="register-button"
            id="registerButton"
          >
            {buttonsSettings.registerButtonName}
          </Button>
          <Button
            href={buttonsSettings.loginLink}
            onClick={(e) =>
              buttonsSettings.onLoginClick && buttonsSettings.onLoginClick(e)
            }
            color="danger"
            size="medium"
            data-testid="login-button"
            id="loginButton"
          >
            {buttonsSettings.loginButtonName}
          </Button>
        </>
      )) ||
        null
    : null;
};

export default TopBar;
